import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef, type ReactNode } from "react";
import { cn } from "../lib/cn";

export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;

export interface DrawerContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  title: string;
  subtitle?: string;
  /** Header actions rendered before the close button. */
  actions?: ReactNode;
  /** Optional tab strip or toolbar rendered under the header. */
  toolbar?: ReactNode;
  footer?: ReactNode;
  width?: number;
}

/** Right-side drawer for detail/edit flows (job, governance, alert rule…). */
export const DrawerContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(
  (
    { className, title, subtitle, actions, toolbar, footer, width = 560, children, ...props },
    ref,
  ) => (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-scrim/80" />
      <DialogPrimitive.Content
        ref={ref}
        style={{ width, maxWidth: "calc(100vw - 32px)" }}
        className={cn(
          "fixed inset-y-0 right-0 z-50 flex flex-col bg-surface shadow-drawer focus-visible:outline-none " +
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-right data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right duration-200",
          className,
        )}
        {...props}
      >
        <div className="flex items-center gap-2.5 border-b border-border-subtle px-5 py-3.5">
          <div className="flex min-w-0 flex-1 flex-col">
            <DialogPrimitive.Title className="text-md font-semibold text-fg">
              {title}
            </DialogPrimitive.Title>
            {subtitle ? (
              <DialogPrimitive.Description className="truncate font-mono text-sm text-fg-muted">
                {subtitle}
              </DialogPrimitive.Description>
            ) : (
              <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
            )}
          </div>
          {actions}
          <DialogPrimitive.Close
            aria-label="Close"
            className="flex size-7 items-center justify-center rounded-sm text-fg-muted hover:bg-surface-inset hover:text-fg"
          >
            <X className="size-4" aria-hidden />
          </DialogPrimitive.Close>
        </div>
        {toolbar}
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex items-center gap-2 border-t border-border-subtle bg-surface-subtle px-5 py-3">
            {footer}
          </div>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  ),
);
DrawerContent.displayName = "DrawerContent";
